import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

/* Context that wrapped 2D graph viz components (HyperGraph etc.) can
 * read to surface their node-clicks to the modal floater. When NOT in
 * a modal the context is null and components fall back to their own
 * onNodeClick prop. */
interface GraphFrameContextValue { setSelectedNodeId: (id: string | null) => void }
const GraphFrameContext = createContext<GraphFrameContextValue | null>(null);
export function useGraphFrame(): GraphFrameContextValue | null { return useContext(GraphFrameContext); }
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Maximize2, X, Box, LayoutGrid, ArrowRight, ArrowLeft } from "lucide-react";
import { cls } from "../../lib/cls";
import { Graph3D, LAYOUT_OPTIONS, type Graph3DNode, type Graph3DLink, type Graph3DLayout } from "./Graph3D";
import { NodeKindIcon } from "./NodeKindIcon";

/* Expandable graph frame — wraps any 2D graph viz with an "Expand"
 * button that opens it in a fullscreen Apple-style modal. Inside the
 * modal a 2D/3D toggle switches the rendering: same data, two views.
 *
 * The 3D view uses react-force-graph-3d (three.js WebGL); the 2D view
 * shows the original children unchanged. Every graph in the product
 * (AgenticMesh · OntologyMap · FlowGraph · IntegrationFlowGraph)
 * wraps with this — single source of expand UX.
 */

interface ExpandableGraphFrameProps {
  title: string;
  subtitle?: string;
  children: ReactNode;       // the 2D graph
  /** Provide if 3D mode is available for this graph. */
  graph3d?: {
    nodes: readonly Graph3DNode[];
    links: readonly Graph3DLink[];
    palette?: Record<string, string>;
  };
  /** Hide the expand button (for places where it's already inside a sheet). */
  hideExpand?: boolean;
}

export function ExpandableGraphFrame({
  title, subtitle, children, graph3d, hideExpand,
}: ExpandableGraphFrameProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<"2d" | "3d">("2d");
  const [layout, setLayout] = useState<Graph3DLayout>("force");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Look up the selected node + its incoming/outgoing edges from the
  // graph3d data — same source the 3D view uses, so the floater is
  // always consistent with what's on screen.
  const selectedNode = useMemo(
    () => (selectedId && graph3d ? graph3d.nodes.find((n) => n.id === selectedId) ?? null : null),
    [selectedId, graph3d],
  );
  const incoming = useMemo(
    () => (selectedId && graph3d ? graph3d.links.filter((l) => l.target === selectedId) : []),
    [selectedId, graph3d],
  );
  const outgoing = useMemo(
    () => (selectedId && graph3d ? graph3d.links.filter((l) => l.source === selectedId) : []),
    [selectedId, graph3d],
  );
  const nodeById = useMemo(
    () => (graph3d ? new Map(graph3d.nodes.map((n) => [n.id, n])) : new Map()),
    [graph3d],
  );

  // ESC closes; respect focus management.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <div className="relative group">
      {!hideExpand ? (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setOpen(true); }}
          className={cls(
            "absolute top-2 right-2 z-10 inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[10.5px] font-medium",
            "bg-cream/85 backdrop-blur-md border border-border/70 text-ink-700 shadow-[0_1px_2px_rgba(0,0,0,0.04)]",
            "hover:text-ink-950 hover:border-foyr/40 hover:shadow-sm transition-shadow",
          )}
          aria-label="Expand graph"
          title="Expand · 2D / 3D"
        >
          <Maximize2 className="w-3 h-3" />
          Expand
        </button>
      ) : null}
      {children}

      {open ? createPortal(
        <AnimatePresence>
          <motion.div
            key="overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="fixed inset-0 z-50 bg-ink-950/60 backdrop-blur-md grid place-items-center px-4 py-4"
            onClick={() => setOpen(false)}
            role="dialog"
            aria-modal="true"
            aria-label={`${title} — expanded view`}
          >
            <motion.div
              initial={{ opacity: 0, y: 16, scale: 0.985 }}
              animate={{ opacity: 1, y: 0,  scale: 1 }}
              exit={{   opacity: 0, y: 16, scale: 0.985 }}
              transition={{ duration: 0.24, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-[min(1400px,96vw)] h-[min(900px,92vh)] rounded-3xl bg-cream border border-border/70 shadow-[0_24px_60px_-12px_rgba(0,0,0,0.30)] overflow-hidden flex flex-col"
            >
              {/* Top bar — title + view toggle + close */}
              <header className="flex items-start justify-between gap-3 px-5 py-3 border-b border-border/40">
                <div className="min-w-0">
                  <h3 className="font-newyork text-[18px] font-semibold text-ink-950 leading-tight tracking-tight truncate">{title}</h3>
                  {subtitle ? (
                    <div className="text-[11.5px] text-ink-500 mt-0.5 truncate">{subtitle}</div>
                  ) : null}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {/* Layout dropdown — only meaningful in 3D mode. */}
                  {graph3d && view === "3d" ? (
                    <select
                      value={layout}
                      onChange={(e) => setLayout(e.target.value as Graph3DLayout)}
                      className="rounded-full bg-cream-dark border border-border px-2.5 py-1 text-[11px] text-ink-700 hover:border-foyr/40 focus:outline-none focus:ring-2 focus:ring-foyr/30"
                      title="Layout algorithm"
                    >
                      {LAYOUT_OPTIONS.map((o) => (
                        <option key={o.id} value={o.id}>{o.label}</option>
                      ))}
                    </select>
                  ) : null}
                  {graph3d ? (
                    <div className="inline-flex rounded-full bg-cream-dark border border-border p-0.5 text-[11px]">
                      <button
                        type="button"
                        onClick={() => setView("2d")}
                        className={cls(
                          "inline-flex items-center gap-1 px-2.5 py-1 rounded-full transition-colors",
                          view === "2d" ? "bg-ink-950 text-cream" : "text-ink-600 hover:text-ink-950",
                        )}
                      >
                        <LayoutGrid className="w-3 h-3" /> 2D
                      </button>
                      <button
                        type="button"
                        onClick={() => setView("3d")}
                        className={cls(
                          "inline-flex items-center gap-1 px-2.5 py-1 rounded-full transition-colors",
                          view === "3d" ? "bg-ink-950 text-cream" : "text-ink-600 hover:text-ink-950",
                        )}
                      >
                        <Box className="w-3 h-3" /> 3D
                      </button>
                    </div>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="grid place-items-center w-8 h-8 rounded-full text-ink-500 hover:text-ink-950 hover:bg-cream-dark transition-colors"
                    aria-label="Close"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </header>

              {/* Body — 2D shows children full-bleed, 3D shows Graph3D */}
              <div className="flex-1 relative overflow-hidden p-4 bg-gradient-to-br from-cream via-cream to-cream-dark/30">
                <AnimatePresence mode="wait">
                  {view === "2d" || !graph3d ? (
                    <motion.div
                      key="2d"
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.22 }}
                      className="w-full h-full overflow-auto"
                    >
                      <GraphFrameContext.Provider value={{ setSelectedNodeId: setSelectedId }}>
                        <div className="w-full h-full [&_.react-flow]:!h-full [&>*]:!h-full [&_section]:!h-full">
                          {children}
                        </div>
                      </GraphFrameContext.Provider>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="3d"
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.22 }}
                      className="w-full h-full"
                    >
                      <Graph3D
                        nodes={graph3d.nodes}
                        links={graph3d.links}
                        palette={graph3d.palette ?? {}}
                        height={800}
                        layout={layout}
                        focusedId={selectedId}
                        onNodeClick={(n) => setSelectedId(n.id)}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Floating node-detail card — appears when a 3D node
                    is clicked. Apple-style: glass panel, soft shadow,
                    slides in from the right. */}
                <AnimatePresence>
                  {selectedNode ? (
                    <motion.aside
                      key={selectedNode.id}
                      initial={{ opacity: 0, x: 24 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{   opacity: 0, x: 24 }}
                      transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
                      className="absolute top-4 right-4 bottom-4 w-[320px] rounded-2xl bg-cream/95 backdrop-blur-md border border-border/70 shadow-[0_12px_36px_-12px_rgba(0,0,0,0.18)] overflow-y-auto"
                    >
                      <div className="px-4 pt-4 pb-3 sticky top-0 bg-cream/95 backdrop-blur-md border-b border-border/40">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-start gap-2.5 min-w-0">
                            <span
                              className="grid place-items-center w-9 h-9 rounded-xl bg-cream-dark/60 shrink-0 ring-1 ring-border/70 shadow-[0_1px_2px_rgba(0,0,0,0.04)]"
                              style={{ color: graph3d?.palette?.[selectedNode.kind] ?? "#141612" }}
                            >
                              <NodeKindIcon kind={selectedNode.kind} size={22} weight="duotone" />
                            </span>
                            <div className="min-w-0">
                              <div className="text-[10px] uppercase tracking-wider text-ink-500 font-mono">{selectedNode.kind}</div>
                              <h4 className="font-newyork text-[17px] font-semibold text-ink-950 leading-tight tracking-tight mt-0.5 break-words">
                                {selectedNode.label}
                              </h4>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => setSelectedId(null)}
                            className="grid place-items-center w-7 h-7 rounded-full text-ink-500 hover:text-ink-950 hover:bg-cream-dark shrink-0"
                            aria-label="Close detail"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        {selectedNode.description ? (
                          <p className="text-[11.5px] text-ink-600 leading-snug mt-1.5">{selectedNode.description}</p>
                        ) : null}
                        <div className="text-[10px] font-mono text-ink-400 mt-1.5">id · {selectedNode.id}</div>
                      </div>
                      <div className="px-4 py-3 space-y-3">
                        {outgoing.length > 0 ? (
                          <section>
                            <div className="text-[10px] uppercase tracking-wider text-ink-500 mb-1.5 inline-flex items-center gap-1">
                              <ArrowRight className="w-3 h-3" /> outgoing ({outgoing.length})
                            </div>
                            <ul className="space-y-1">
                              {outgoing.slice(0, 24).map((l, i) => {
                                const target = nodeById.get(typeof l.target === "string" ? l.target : (l.target as { id: string }).id);
                                return (
                                  <li
                                    key={`o-${i}`}
                                    className="text-[11.5px] flex items-center gap-1.5 cursor-pointer rounded-md px-1.5 py-1 hover:bg-cream-dark"
                                    onClick={() => target && setSelectedId(target.id)}
                                  >
                                    <span className="text-ink-500 font-mono text-[10px] shrink-0">{l.label ?? "→"}</span>
                                    <ArrowRight className="w-2.5 h-2.5 text-ink-400 shrink-0" />
                                    <span className="text-ink-950 truncate">{target?.label ?? l.target}</span>
                                  </li>
                                );
                              })}
                            </ul>
                          </section>
                        ) : null}
                        {incoming.length > 0 ? (
                          <section>
                            <div className="text-[10px] uppercase tracking-wider text-ink-500 mb-1.5 inline-flex items-center gap-1">
                              <ArrowLeft className="w-3 h-3" /> incoming ({incoming.length})
                            </div>
                            <ul className="space-y-1">
                              {incoming.slice(0, 24).map((l, i) => {
                                const source = nodeById.get(typeof l.source === "string" ? l.source : (l.source as { id: string }).id);
                                return (
                                  <li
                                    key={`i-${i}`}
                                    className="text-[11.5px] flex items-center gap-1.5 cursor-pointer rounded-md px-1.5 py-1 hover:bg-cream-dark"
                                    onClick={() => source && setSelectedId(source.id)}
                                  >
                                    <span className="text-ink-950 truncate">{source?.label ?? l.source}</span>
                                    <ArrowRight className="w-2.5 h-2.5 text-ink-400 shrink-0" />
                                    <span className="text-ink-500 font-mono text-[10px] shrink-0">{l.label ?? "→"}</span>
                                  </li>
                                );
                              })}
                            </ul>
                          </section>
                        ) : null}
                        {outgoing.length === 0 && incoming.length === 0 ? (
                          <div className="text-[11px] text-ink-500">No edges in the rendered slice.</div>
                        ) : null}
                      </div>
                    </motion.aside>
                  ) : null}
                </AnimatePresence>
              </div>
            </motion.div>
          </motion.div>
        </AnimatePresence>,
        document.body,
      ) : null}
    </div>
  );
}
